import {
  applyAgentWorkspaceSemanticPatchToProject,
  createAgentWorkspaceSemanticPatchProposal,
  ensureAgentWorkspaceRequirement,
  type AgentWorkspaceAnchor,
  type AgentWorkspaceRequirement,
  type AgentWorkspaceSemanticPatch,
} from '@/features/agent-workspace'
import {
  findCogoCollabTransactionForRequirement,
  transitionCogoCollabTransactionInProject,
} from '@/features/collaboration-kernel'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import type { LoadedApp } from '@/features/page-editor/store/types'
import type { ChannelOutput } from '@/features/_shared/channel-types'
import { useStudioStore } from '@/store/studio-store'
import type { IntentPreviewData } from '../hooks/use-intent-preview'

function severityFromRisk(
  risk: AgentWorkspaceSemanticPatch['risk'],
): IntentPreviewData['severity'] {
  return risk
}

function uniqueEvidenceRefs(values: string[]): string[] {
  return values.filter((value, index, items) => items.indexOf(value) === index)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function escapeJsonPointerPart(value: string): string {
  return value.replace(/~/g, '~0').replace(/\//g, '~1')
}

function parseStudioPreviewAnchorId(anchorId: string | undefined): {
  blockId?: string
  elementId?: string
} {
  if (!anchorId?.startsWith('studio.preview.')) return {}
  const parts = anchorId.split('.')
  if (parts.length < 4) return {}
  return {
    blockId: parts[2],
    elementId: parts.slice(3).join('.'),
  }
}

interface PreviewClassNamePatchRule {
  ruleId: string
  keywordGroups: RegExp[][]
  addTokens: string[]
  removeTokenPatterns: RegExp[]
  targetTypes?: string[]
}

const PREVIEW_CLASSNAME_PATCH_RULES: PreviewClassNamePatchRule[] = [
  {
    ruleId: 'preview.spacing.inside-horizontal-padding',
    keywordGroups: [
      [/여백/i, /간격/i, /\bpadding\b/i, /\bspacing\b/i, /\binset\b/i],
      [
        /왼쪽/i,
        /좌측/i,
        /안쪽/i,
        /카드/i,
        /\bleft\b/i,
        /\binside\b/i,
        /\bcard\b/i,
        /\bhorizontal\b/i,
      ],
    ],
    addTokens: ['px-6'],
    removeTokenPatterns: [/^px-\d+$/, /^px-\[.+\]$/, /^pl-\d+$/, /^pr-\d+$/],
    targetTypes: ['container', 'card'],
  },
  {
    ruleId: 'preview.spacing.following-text-gap',
    keywordGroups: [
      [/그림/i, /이미지/i, /\bimage\b/i, /\bpicture\b/i],
      [/다음/i, /아래/i, /뒤/i, /\bafter\b/i, /\bbelow\b/i],
      [/간격/i, /여백/i, /\bgap\b/i, /\bspace\b/i, /\bmargin\b/i],
    ],
    addTokens: ['mt-4'],
    removeTokenPatterns: [/^mt-\d+$/, /^mt-\[.+\]$/],
  },
  {
    ruleId: 'preview.alignment.text-center',
    keywordGroups: [
      [/가운데/i, /중앙/i, /\bcenter\b/i],
      [/정렬/i, /\balign\b/i, /\balignment\b/i],
    ],
    addTokens: ['text-center'],
    removeTokenPatterns: [/^text-(left|right|center|justify)$/],
  },
  {
    ruleId: 'preview.spacing.container-gap',
    keywordGroups: [
      [/간격/i, /\bgap\b/i, /\bspace\b/i],
      [/넓/i, /늘/i, /더/i, /\bmore\b/i, /\bincrease\b/i],
    ],
    addTokens: ['gap-4'],
    removeTokenPatterns: [/^gap-\d+$/, /^gap-\[.+\]$/],
    targetTypes: ['container', 'card'],
  },
]

function findElementPath(
  node: unknown,
  elementId: string,
  path: string[] = [],
): string[] | null {
  if (Array.isArray(node)) {
    for (let index = 0; index < node.length; index += 1) {
      const found = findElementPath(node[index], elementId, [
        ...path,
        String(index),
      ])
      if (found) return found
    }
    return null
  }

  if (!isRecord(node)) return null
  if (node.id === elementId) return path

  const children = node.children
  if (Array.isArray(children)) {
    const found = findElementPath(children, elementId, [...path, 'children'])
    if (found) return found
  }

  return null
}

function findTextElementPath(
  node: unknown,
  elementId: string,
  path: string[] = [],
): string[] | null {
  if (Array.isArray(node)) {
    for (let index = 0; index < node.length; index += 1) {
      const found = findTextElementPath(node[index], elementId, [
        ...path,
        String(index),
      ])
      if (found) return found
    }
    return null
  }

  if (!isRecord(node)) return null
  if (node.id === elementId && typeof node.text === 'string') {
    return path
  }

  const children = node.children
  if (Array.isArray(children)) {
    const found = findTextElementPath(children, elementId, [
      ...path,
      'children',
    ])
    if (found) return found
  }

  return null
}

function matchesKeywordGroups(
  text: string,
  keywordGroups: RegExp[][],
): boolean {
  return keywordGroups.every((group) =>
    group.some((pattern) => pattern.test(text)),
  )
}

function resolvePreviewClassNamePatchRule(input: {
  requirementBody: string
  element: Record<string, unknown>
}): PreviewClassNamePatchRule | null {
  const elementType =
    typeof input.element.type === 'string' ? input.element.type : undefined
  return (
    PREVIEW_CLASSNAME_PATCH_RULES.find((rule) => {
      if (
        rule.targetTypes &&
        (!elementType || !rule.targetTypes.includes(elementType))
      ) {
        return false
      }
      return matchesKeywordGroups(input.requirementBody, rule.keywordGroups)
    }) ?? null
  )
}

function applyClassNameRule(
  className: string,
  rule: PreviewClassNamePatchRule,
): string {
  const existingTokens = className.split(/\s+/).filter(Boolean)
  const retainedTokens = existingTokens.filter(
    (token) =>
      !rule.removeTokenPatterns.some((pattern) => pattern.test(token)) &&
      !rule.addTokens.includes(token),
  )
  return [...retainedTokens, ...rule.addTokens].join(' ').trim()
}

function readAtPath(root: unknown, path: string[]): unknown {
  return path.reduce<unknown>((cursor, part) => {
    if (Array.isArray(cursor)) return cursor[Number(part)]
    if (isRecord(cursor)) return cursor[part]
    return undefined
  }, root)
}

function writeAtPath(
  root: unknown,
  path: string[],
  key: string,
  value: unknown,
) {
  const target = readAtPath(root, path)
  if (!isRecord(target)) return false
  target[key] = value
  return true
}

function isApplicationPreviewChange(preview: IntentPreviewData): boolean {
  return (
    preview.anchor?.kind === 'application.block' ||
    preview.semantic_patch?.target_kind === 'application.block'
  )
}

function applyApprovedPreviewTextChange(input: {
  project: Record<string, unknown>
  preview: IntentPreviewData
}): { project: Record<string, unknown>; changedPaths: string[] } | null {
  if (!isApplicationPreviewChange(input.preview)) return null

  const nextText =
    input.preview.requirement?.proposal?.mode === 'text-replacement'
      ? input.preview.requirement.proposal.body.trim()
      : ''
  if (!nextText) return null

  const parsedAnchor = parseStudioPreviewAnchorId(
    input.preview.anchor?.anchor_id,
  )
  const elementId =
    input.preview.semantic_patch?.target || parsedAnchor.elementId || undefined
  if (!elementId) return null

  const project = cloneJson(input.project)
  const blocks = isRecord(project.blocks) ? project.blocks : {}
  const preferredBlockId = parsedAnchor.blockId
  const candidateBlockIds = [
    ...(preferredBlockId && preferredBlockId in blocks
      ? [preferredBlockId]
      : []),
    ...Object.keys(blocks).filter((blockId) => blockId !== preferredBlockId),
  ]

  for (const blockId of candidateBlockIds) {
    const block = blocks[blockId]
    if (!isRecord(block)) continue
    const ui = isRecord(block.ui) ? block.ui : null
    const root = ui?.root
    const elementPath = findTextElementPath(root, elementId)
    if (!elementPath) continue

    const currentElement = readAtPath(root, elementPath)
    if (!isRecord(currentElement) || currentElement.text === nextText) {
      return null
    }

    if (!writeAtPath(root, elementPath, 'text', nextText)) return null
    const changedPath = [
      'blocks',
      blockId,
      'ui',
      'root',
      ...elementPath,
      'text',
    ]
      .map(escapeJsonPointerPart)
      .join('/')
    return {
      project,
      changedPaths: [`/${changedPath}`],
    }
  }

  return null
}

function applyApprovedPreviewClassNameChange(input: {
  project: Record<string, unknown>
  preview: IntentPreviewData
}): { project: Record<string, unknown>; changedPaths: string[] } | null {
  if (!isApplicationPreviewChange(input.preview)) return null

  const requirementBody = input.preview.requirement?.body.trim()
  if (!requirementBody) return null

  const parsedAnchor = parseStudioPreviewAnchorId(
    input.preview.anchor?.anchor_id,
  )
  const elementId =
    input.preview.semantic_patch?.target || parsedAnchor.elementId || undefined
  if (!elementId) return null

  const project = cloneJson(input.project)
  const blocks = isRecord(project.blocks) ? project.blocks : {}
  const preferredBlockId = parsedAnchor.blockId
  const candidateBlockIds = [
    ...(preferredBlockId && preferredBlockId in blocks
      ? [preferredBlockId]
      : []),
    ...Object.keys(blocks).filter((blockId) => blockId !== preferredBlockId),
  ]

  for (const blockId of candidateBlockIds) {
    const block = blocks[blockId]
    if (!isRecord(block)) continue
    const ui = isRecord(block.ui) ? block.ui : null
    const root = ui?.root
    const elementPath = findElementPath(root, elementId)
    if (!elementPath) continue

    const currentElement = readAtPath(root, elementPath)
    if (!isRecord(currentElement)) continue

    const rule = resolvePreviewClassNamePatchRule({
      requirementBody,
      element: currentElement,
    })
    if (!rule) return null

    const currentClassName =
      typeof currentElement.className === 'string'
        ? currentElement.className
        : ''
    const nextClassName = applyClassNameRule(currentClassName, rule)
    if (!nextClassName || nextClassName === currentClassName) return null

    if (!writeAtPath(root, elementPath, 'className', nextClassName)) {
      return null
    }
    const changedPath = [
      'blocks',
      blockId,
      'ui',
      'root',
      ...elementPath,
      'className',
    ]
      .map(escapeJsonPointerPart)
      .join('/')
    return {
      project,
      changedPaths: [`/${changedPath}`],
    }
  }

  return null
}

function syncRenderedOutputWithProject(
  output: ChannelOutput | null,
  project: Record<string, unknown>,
): ChannelOutput | null {
  if (!output) return output
  const manifest = project.manifest
  const blocks = project.blocks

  if (!isRecord(manifest) || !isRecord(blocks)) return output

  if (output.kind === 'rendered') {
    return {
      ...output,
      manifest: manifest as unknown as LoadedApp['manifest'],
      blocks: blocks as unknown as LoadedApp['blocks'],
    }
  }

  if (output.kind === 'shaclFail' && output.partialResult) {
    return {
      ...output,
      partialResult: {
        ...output.partialResult,
        manifest: manifest as unknown as LoadedApp['manifest'],
        blocks: blocks as unknown as LoadedApp['blocks'],
      },
    }
  }

  return output
}

function syncEditorStoreWithProject(project: Record<string, unknown>) {
  if (!isRecord(project.manifest) || !isRecord(project.blocks)) return
  useEditorStore.getState().loadApp({
    manifest: project.manifest,
    blocks: project.blocks,
    data: isRecord(project.data) ? project.data : {},
    flows: isRecord(project.flows) ? project.flows : {},
    tokens: isRecord(project.tokens) ? project.tokens : undefined,
    designSystem: isRecord(project.designSystem)
      ? (project.designSystem as Record<string, string>)
      : undefined,
  } as unknown as LoadedApp)
}

export function buildIntentPreviewFromAgentWorkspaceRequirement(input: {
  anchor: AgentWorkspaceAnchor
  requirement: AgentWorkspaceRequirement
  patchId?: string
  createdAt?: string
}): IntentPreviewData {
  const proposedBody =
    input.requirement.proposal?.body ?? input.requirement.body
  const semanticPatch = createAgentWorkspaceSemanticPatchProposal({
    patchId:
      input.patchId ?? `${input.requirement.requirementId}.semantic-patch`,
    requirement: input.requirement,
    anchor: input.anchor,
    createdAt: input.createdAt,
  })
  const evidenceRefs = uniqueEvidenceRefs([
    ...input.anchor.evidenceRefs,
    ...semanticPatch.evidenceRefs,
  ])
  return {
    id: semanticPatch.patchId,
    action_verb: semanticPatch.operation,
    severity: severityFromRisk(semanticPatch.risk),
    diff_rows: [
      {
        field: 'Preview selection',
        before: input.anchor.label,
        after: proposedBody,
      },
    ],
    impact_summary:
      'Use updates the selected Preview value and records proof for review.',
    resolved: false,
    anchor: {
      anchor_id: input.anchor.anchorId,
      label: input.anchor.label,
      kind: input.anchor.kind,
      target_formats: input.anchor.targetFormats,
      evidence_refs: input.anchor.evidenceRefs,
    },
    requirement: {
      requirement_id: input.requirement.requirementId,
      intent: input.requirement.intent,
      body: input.requirement.body,
      acceptance_criteria: input.requirement.acceptanceCriteria,
      ...(input.requirement.proposal
        ? {
            proposal: {
              body: input.requirement.proposal.body,
              source: input.requirement.proposal.source,
              mode: input.requirement.proposal.mode,
            },
          }
        : {}),
    },
    semantic_patch: {
      patch_id: semanticPatch.patchId,
      operation: semanticPatch.operation,
      target: semanticPatch.target,
      target_kind: semanticPatch.targetKind,
      authority: 'semantic-patch-draft',
      risk: semanticPatch.risk,
      validation_plan: semanticPatch.validationPlan,
      evidence_refs: evidenceRefs,
      may_patch_cogo_json_directly: false,
    },
  }
}

export function applyIntentPreviewSemanticPatchToStudioStore(
  preview: IntentPreviewData,
  approvedAt = new Date().toISOString(),
): boolean {
  const semanticPatch = preview.semantic_patch
  const anchor = preview.anchor
  const requirement = preview.requirement
  const state = useStudioStore.getState()
  const project = state.currentProject
  if (!project || !semanticPatch || !anchor || !requirement) return false

  const ensuredRequirement = ensureAgentWorkspaceRequirement({
    project,
    requirementId: requirement.requirement_id,
    anchorId: anchor.anchor_id,
    intent: requirement.intent as AgentWorkspaceRequirement['intent'],
    body: requirement.body,
    acceptanceCriteria: requirement.acceptance_criteria,
    proposal: requirement.proposal,
    createdAt: approvedAt,
  })
  const result = applyAgentWorkspaceSemanticPatchToProject({
    project,
    requirement: ensuredRequirement,
    semanticPatch: {
      schemaVersion: 'cogo.studio-agent-workspace-semantic-patch.v1',
      patchId: semanticPatch.patch_id,
      requirementId: requirement.requirement_id,
      anchorId: anchor.anchor_id,
      status: 'approved',
      operation: semanticPatch.operation,
      target: semanticPatch.target,
      targetKind: semanticPatch.target_kind,
      authority: 'semantic-patch-draft',
      risk: semanticPatch.risk,
      validationPlan: semanticPatch.validation_plan,
      evidenceRefs: semanticPatch.evidence_refs,
      createdAt: approvedAt,
      approvedAt,
      mayPatchCogoJsonDirectly: false,
    },
    approvedAt,
  })
  const collaborationTransaction = findCogoCollabTransactionForRequirement(
    result.project,
    requirement.requirement_id,
  )
  const ledgerProject = collaborationTransaction
    ? transitionCogoCollabTransactionInProject({
        project: result.project,
        transactionId: collaborationTransaction.id,
        status: 'applied',
        userEvent: 'proposal.apply',
        updatedAt: approvedAt,
      })
    : result.project
  const previewStructureChange =
    applyApprovedPreviewClassNameChange({
      project: ledgerProject,
      preview,
    }) ??
    applyApprovedPreviewTextChange({
      project: ledgerProject,
      preview,
    })
  const nextProject = previewStructureChange?.project ?? ledgerProject
  const parsedPreviewAnchor = parseStudioPreviewAnchorId(anchor.anchor_id)
  const previousContext = state.chatContext ?? { mode: 'chat' as const }
  const nextChatContext = {
    ...previousContext,
    selectedBlockName:
      parsedPreviewAnchor.blockId ?? previousContext.selectedBlockName,
    selectedElementId:
      parsedPreviewAnchor.elementId ??
      semanticPatch.target ??
      previousContext.selectedElementId,
    selectedElementLabel: anchor.label,
  }

  useStudioStore.setState({
    currentProject: nextProject as never,
    dispatchedOutput: syncRenderedOutputWithProject(
      state.dispatchedOutput,
      nextProject,
    ) as never,
    chatContext: nextChatContext as never,
    isDirty: true,
  } as never)
  if (previewStructureChange) {
    syncEditorStoreWithProject(nextProject)
  }

  const transaction = state.agenticUxTransaction
  if (transaction?.status === 'running') {
    state.advanceAgenticUxTransaction(transaction.id, {
      phase: 'render-committed',
      lastSpec: requirement.body,
      reference: `${anchor.label} / ${semanticPatch.operation}`,
    })
  }

  return true
}

export function rejectIntentPreviewProposalInStudioStore(
  preview: IntentPreviewData,
  rejectedAt = new Date().toISOString(),
): boolean {
  const requirement = preview.requirement
  const state = useStudioStore.getState()
  const project = state.currentProject
  if (!project || !requirement) return false

  const collaborationTransaction = findCogoCollabTransactionForRequirement(
    project,
    requirement.requirement_id,
  )
  if (!collaborationTransaction) return false

  const nextProject = transitionCogoCollabTransactionInProject({
    project,
    transactionId: collaborationTransaction.id,
    status: 'rejected',
    userEvent: 'proposal.reject',
    updatedAt: rejectedAt,
  })

  useStudioStore.setState({
    currentProject: nextProject as never,
    isDirty: true,
  } as never)

  return true
}
